import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

// シーンの設定
const scene = new THREE.Scene();
const aspect = window.innerWidth / window.innerHeight;
const camera = new THREE.PerspectiveCamera(45, aspect, 0.4, 10000);

// 環境光の設定
const ambientLight = new THREE.AmbientLight(0xf9f9ff, 1.4);
scene.add(ambientLight);

// メイン光源（正面）
const mainLight = new THREE.DirectionalLight(0xffffff, 1.5);
mainLight.position.set(0, 0, 3);
mainLight.castShadow = true;
mainLight.shadow.mapSize.width = 2048;
mainLight.shadow.mapSize.height = 2048;
mainLight.shadow.bias = 0.000001;
scene.add(mainLight);

// 補助光源1（右側）
const fillLight1 = new THREE.DirectionalLight(0x99ccff, 2.0);
fillLight1.position.set(3, 2, 0);
fillLight1.castShadow = true;
fillLight1.shadow.mapSize.width = 2048;
fillLight1.shadow.mapSize.height = 2048;
fillLight1.shadow.bias = 0.000001;
scene.add(fillLight1);

// 補助光源2（左側）
const fillLight2 = new THREE.DirectionalLight(0x99ccff, 1.8);
fillLight2.position.set(-3, 2, 0);
fillLight2.castShadow = true;
fillLight2.shadow.mapSize.width = 2048;
fillLight2.shadow.mapSize.height = 2048;
fillLight2.shadow.bias = 0.0000001;
scene.add(fillLight2);

// アニメーションループ
function animate() {
    requestAnimationFrame(animate);
    
    // 照明のアニメーション
    const time = Date.now() * 0.001;
    const pulse = Math.sin(time) * 0.1 + 1.0;
    
    // 光源の位置を動かす
    mainLight.position.set(
        Math.sin(time) * 2,
        Math.cos(time) * 2,
        Math.tan(time) * 2
    );
    
    fillLight1.position.set(
        Math.tan(time) * 2,
        Math.sin(time) * 2,
        Math.cos(time) * 2
    );
    
    fillLight2.position.set(
        -5,
        Math.sin(time) * 2,
        -Math.cos(time) * 2
    );
    
    renderer.render(scene, camera);
}

// レンダラーの設定
const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true
});
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.shadowMap.bias = 0.001;
document.body.appendChild(renderer.domElement);

// コントロールの設定
const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.07;
controls.rotateSpeed = 0.5;
controls.enableZoom = true;
controls.enablePan = true;

const model = new THREE.MeshStandardMaterial({
    metalnessMap: null,
    roughnessMap: null,
    normalMap: null,
    bumpScale: 0.5,
    displacementScale: 0.6,
    displacementBias: 2
});

// キューブの設定（テスト用）
const geometry = new THREE.BoxGeometry(0, 0, 0);
const material = new THREE.MeshNormalMaterial();
const cube = new THREE.Mesh(geometry, material);
cube.position.set(0, 0.3, 0); 
cube.castShadow = true;
cube.receiveShadow = true;
scene.add(cube);

// GLTFモデル読み込み
// GLTFモデル読み込み時の設定
function loadData() {
    const loader = new GLTFLoader()
        .setPath('assets/models/')
        .load('kin_umi.gltf', 
            (gltf) => {
                const model = gltf.scene;

// テクスチャー読み込み用の関数
function loadTextures(urls) {
    const textures = {};
    const promises = urls.map(url => {
        return new Promise((resolve, reject) => {
            const texture = new THREE.Texture();
            const loader = new THREE.TextureLoader();
            
            loader.load(url, (loadedTexture) => {
                texture.image = loadedTexture.image;
                texture.needsUpdate = true;
                resolve(texture);
            }, undefined, (error) => {
                reject(new Error(`テクスチャー読み込みエラー: ${error}`));
            });
        });
    });
    
    return Promise.all(promises);
}

// テクスチャーのURL配列
const textureUrls = [
    './dist/assets/models/kizu_normal.png',
    './dist/assets/models/image_1-1.png',
    './dist/assets/models/image_0.png',
    './dist/assets/models/kizu_diffuseOriginal.png',
    './dist/assets/models/kizu_height.png',
    './dist/assets/models/kizu_ao.png',
    './dist/assets/models/image_1.png'    
];

// テクスチャー読み込みとメッシュ作成
async function createMeshes() {
    try {
        const textures = await loadTextures(textureUrls);
        
        // メッシュの作成
        const meshes = textures.map((texture, index) => {
            // テクスチャーの設定
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
            texture.repeat.set(1, 1);
            
            // マテリアルの作成
            const material = new THREE.MeshStandardMaterial({
                map: texture,
                metalness: 0.3,
                roughness: 0.5
            });
            
            // ジオメトリの作成
            const geometry = new THREE.BoxGeometry(1, 1, 1);
            
            // メッシュの作成と位置設定
            const mesh = new THREE.Mesh(geometry, material);
            mesh.position.set(
                (index % 3) * 2 - 2, // x座標
                Math.floor(index / 3) * 2, // y座標
                0 // z座標
            );
            
            return mesh;
        });
        
        // シーンに追加
        meshes.forEach(mesh => {
            scene.add(mesh);
        });
        
    } catch (error) {
        console.error('テクスチャー読み込みエラー:', error);
    }
}

// メイン処理
createMeshes();
                
// マテリアルの読み込み
function gltfReader(gltf) {
    const model = gltf.scene;
    model.traverse((child) => {
        if (child.isMesh) {
            child.material = new THREE.MeshStandardMaterial({
                metalness: 0.1,
                roughness: 0.5
            });
            // メッシュの位置を直接調整する方法
model.traverse((child) => {
    if (child.isMesh) {
        // 現在の位置を取得して反転
        const currentPosition = child.position.clone();
        child.position.set(
            -currentPosition.x,
            -currentPosition.y,
            -currentPosition.z
        );
    }
});
            child.castShadow = true;
            child.receiveShadow = true;
        }
    });
    scene.add(model);
}
const geometry = new THREE.TorusGeometry(300, 100, 64, 100);
const material = new THREE.MeshStandardMaterial({
    color: 0xff99FF,
    roughness: 0.3,      // 光沢度（0.0～1.0）
    metalness: 0.1,      // 金属性（0.0～1.0）
    map: null,           // テクスチャ
    normalMap: null,     // ノーマルマップ
    bumpScale: 1         // バンプマッピングの強さ
});
const mesh = new THREE.Mesh(geometry, material);
                // モデルの全てのメッシュに影の設定を適用
                model.traverse((child) => {
                    if (child.isMesh) {
                        child.castShadow = true;    // 影を投げる
                        child.receiveShadow = true; // 影を受け取る
                    }
                });
                

                scene.add(model);
                console.log("モデル読み込み完了");
            },
            (progress) => {
                console.log("読み込み中: " + (progress.loaded / progress.total * 100) + "%");
            },
            (error) => {
                console.error("エラーが発生しました: ", error);
            }
        );
}

// カメラの位置と向きを設定
camera.position.set(0, 0, 2); // z軸を5単位後ろに移動
camera.lookAt(0, 0, 1); // 原点を向く

// カメラの視野角を調整
camera.fov = 45; // 視野角を45度に設定
camera.updateProjectionMatrix(); // プロジェクション行列を更新

// アニメーションループ
const clock = new THREE.Clock();
function animate() {
    requestAnimationFrame(animate);
    
    const delta = clock.getDelta();
    const elapsed = clock.getElapsedTime();
    
    // キューブの回転
    cube.rotation.x = elapsed / 2;
    cube.rotation.y = elapsed / 1;
    
    // コントロールの更新
    controls.update();
    
    renderer.render(scene, camera);
}

// 初期化
loadData();
animate();